import React from 'react';
import { Link } from 'react-router-dom';
import { InkSection } from './shared';
import { useAccount } from '../../context/AccountContext';
import { MediaAllowanceMeters, useMediaAllowance } from '../media/MediaAllowance';

/**
 * Content → Media → Media settings ("tinycms UX optimization review v6",
 * Prototype v6). These are the rules every image and file follows. They are
 * fixed, not switches: publishing is blocked on missing alt text (preflight),
 * originals are never overwritten, and an upload from the browser can be up to
 * BROWSER_UPLOAD_MAX_BYTES (24 MB) on the server.
 */
const RULES: Array<{ title: string; desc: string; value: string }> = [
  {
    title: 'Alt text required',
    desc: 'An image without alt text can’t be published, so screen readers and search can describe it.',
    value: 'Always on',
  },
  {
    title: 'Originals kept',
    desc: 'Crops and resizes are saved as new versions. You can go back to any of them.',
    value: 'Always on',
  },
  {
    title: 'Largest image',
    desc: 'Per file, from this app or a link. Location and camera details are removed before an image is shown.',
    value: '24 MB',
  },
  {
    title: 'Videos',
    desc: 'Upload from this app or have your agent import a link. Each video is prepared for every screen size and plays with a cover image until someone presses play.',
    value: 'Up to 4 GB',
  },
];

export function CmsMediaSettings() {
  const { accountId } = useAccount();
  const { allowance } = useMediaAllowance(accountId);
  return (
    <div className="ink-body">
      <div className="ink-site-settings-head">
        <Link to="/content?view=media" className="ink-back-link">
          ← Media
        </Link>
        <h1 className="ink-h1">Media settings</h1>
        <p className="ink-lede">Applies to every image and file in Media.</p>
      </div>
      {allowance && (
        <InkSection
          title="Your plan’s allowance"
          description={`${allowance.planLabel ? `On the ${allowance.planLabel} plan. ` : ''}Nothing is removed if you go over — new uploads wait until there’s room. Video watched past what’s included is billed after the month ends.`}
        >
          <div className="ink-site-card" style={{ padding: 16 }}>
            <MediaAllowanceMeters allowance={allowance} />
            <p style={{ marginTop: 12, fontSize: 13 }}>
              <Link to="/settings?section=billing" style={{ color: 'var(--ink-accent)' }}>
                Compare plans →
              </Link>
            </p>
          </div>
        </InkSection>
      )}
      <InkSection
        title="Uploads"
        description="What every file needs before it can appear on the site."
      >
        <div className="ink-site-card">
          {RULES.map((r) => (
            <div key={r.title} className="ink-site-row">
              <span>
                <span className="ink-site-row-title">{r.title}</span>
                <span className="ink-site-row-desc" style={{ display: 'block' }}>
                  {r.desc}
                </span>
              </span>
              <span className="ink-media-rule">{r.value}</span>
            </div>
          ))}
        </div>
      </InkSection>
    </div>
  );
}
